import {
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import React from "react";
import { debounce } from "lodash";
import { updateUserBin } from "../utils";
import {
  Backpack,
  ChevronDown,
  Filter,
  MinusCircle,
  Trash2,
} from "lucide-react";
import { useTheme } from "@mui/material/styles";
import {
  getBorder,
  getMutedText,
  HEADING_FONT,
} from "../constants/colors";

const GearBin = ({
  bin,
  handleDeleteFromBin,
  handleDeleteBin,
  binIndex,
  handleFilterToBin,
  handleAddBinToPack,
  handleUpdateBinText,
  darkMode,
}) => {
  const [actionsAnchorEl, setActionsAnchorEl] = React.useState(null);
  const theme = useTheme();
  const iconColors = {
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
  };

  const handleActionsClick = (e) => {
    setActionsAnchorEl(e.currentTarget);
  };

  const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };

  const columns = [
    {
      field: "item",
      headerName: "Item",
      headerAlign: "left",
      headerClassName: "firstcolumnSeparator",
      flex: 1,
    },
    {
      field: "category",
      headerName: "Item Type",
      minWidth: 95,
      flex: 0.65,
      headerAlign: "left",
    },
    {
      field: "weight",
      headerName: "Weight",
      type: "number",
      width: 80,
      headerAlign: "right",
      headerClassName: "lastcolumnSeparator",
      sortable: false,
    },
    {
      field: "unit",
      headerName: "",
      type: "singleSelect",
      width: 69,
      height: "36px",
      valueOptions: ["g", "oz", "lb", "kg"],
      headerAlign: "left",
      sortable: false,
    },
    {
      field: "price",
      headerName: "Price",
      width: 92,
      type: "number",
      headerAlign: "right",
      renderCell: (params) => {
        return Number(params.value).toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        });
      },
    },
    {
      field: "action",
      headerName: "",
      width: 40,
      headerClassName: "lastcolumnSeparator",
      headerAlign: "left",
      sortable: false,
      renderCell: (params) => {
        return (
          <>
            <IconButton
              sx={{
                marginLeft: "5px",
                height: "30px",
                width: "30px",
              }}
              onClick={() => {
                handleDeleteFromBin(binIndex, params.row.id);
              }}
            >
              <Tooltip title="Delete from this bin">
                <MinusCircle size={18} color={iconColors.warning} />
              </Tooltip>
            </IconButton>
          </>
        );
      },
    },
  ];

  const debouncedTitleChange = debounce((e) => {
    (async () => {
      await updateUserBin(bin.id, { title: e.target.value });
      handleUpdateBinText(binIndex, "title", e.target.value);
    })();
  }, 1000);

  const debouncedSubtitleChange = debounce((e) => {
    (async () => {
      await updateUserBin(bin.id, { subtitle: e.target.value });
      handleUpdateBinText(binIndex, "subtitle", e.target.value);
    })();
  }, 1000);

  return (
    <Paper
      elevation={0}
      sx={{
        padding: "12px",
        borderRadius: "12px",
        border: `1px solid ${getBorder(darkMode)}`,
        backgroundImage: "none",
      }}
    >
      <Stack>
        <Stack direction="row" justifyContent="space-between">
          <TextField
            defaultValue={bin.title}
            key={bin.title}
            size="small"
            variant="standard"
            InputProps={{
              disableUnderline: true,
            }}
            onChange={debouncedTitleChange}
            sx={{
              width: "100%",
              height: "30px",
              paddingLeft: "5px",
              borderRadius: "8px",
              "& .MuiInputBase-input": {
                fontSize: "20px",
                height: "24px",
                fontFamily: HEADING_FONT,
                fontWeight: 700,
                letterSpacing: "-0.01em",
              },
              "& fieldset": {
                borderColor: "#2F2F2F",
              },
              "&:hover": {
                backgroundColor: darkMode
                  ? "rgba(255,255,255,0.06)"
                  : "rgba(0,0,0,0.04)",
              },
            }}
          />
          <Stack direction="row" spacing={0.5}>
            <Button
              onClick={handleActionsClick}
              endIcon={<ChevronDown size={16} />}
              variant="contained"
              disableElevation
              size="small"
              sx={{
                borderRadius: "9px",
                fontFamily: HEADING_FONT,
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              {"Actions"}
            </Button>
            <Menu
              anchorEl={actionsAnchorEl}
              open={Boolean(actionsAnchorEl)}
              onClose={handleActionsClose}
              PaperProps={{ sx: { borderRadius: "12px" } }}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                onClick={() => {
                  handleFilterToBin(bin.title, bin.contents);
                }}
              >
                <ListItemIcon>
                  <Filter size={18} />
                </ListItemIcon>
                <ListItemText primary="Filter Closet to this Bin" />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleAddBinToPack(bin);
                }}
              >
                <ListItemIcon>
                  <Backpack size={18} />
                </ListItemIcon>
                <ListItemText primary="Add bin to pack as category" />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleDeleteBin(bin.id);
                }}
              >
                <ListItemIcon>
                  <Trash2 size={18} color={iconColors.error} />
                </ListItemIcon>
                <ListItemText primary="Delete Bin" />
              </MenuItem>
            </Menu>
          </Stack>
        </Stack>
        <TextField
          defaultValue={bin.subtitle}
          placeholder="No Description"
          key={bin.subtitle}
          size="small"
          variant="standard"
          InputProps={{
            disableUnderline: true,
          }}
          onChange={debouncedSubtitleChange}
          sx={{
            width: "100%",
            paddingLeft: "5px",
            borderRadius: "8px",
            "& .MuiInputBase-input": {
              fontSize: "16px",
              paddingLeft: "0px",
              fontStyle: "italic",
              fontFamily: HEADING_FONT,
              color: getMutedText(darkMode),
            },
            "& fieldset": {
              borderColor: "#2F2F2F",
            },
            "&:hover": {
              backgroundColor: darkMode
                ? "rgba(255,255,255,0.06)"
                : "rgba(0,0,0,0.04)",
            },
          }}
        />
        <DataGridPro
          sx={{
            borderRadius: "10px",
            borderColor: getBorder(darkMode),
            marginTop: "6px",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontFamily: HEADING_FONT,
              fontWeight: 600,
              fontSize: "11px",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: getMutedText(darkMode),
            },
            "& .MuiDataGrid-columnHeaders": {
              borderColor: getBorder(darkMode),
            },
            "& .MuiDataGrid-cell": {
              borderColor: getBorder(darkMode),
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: darkMode
                ? "rgba(144,202,249,0.06)"
                : "rgba(25,118,210,0.04)",
            },
          }}
          columns={columns}
          rows={bin.contents}
          density="compact"
          hideFooter
          autoHeight
        />
      </Stack>
    </Paper>
  );
};

export default GearBin;
